import { useEffect } from 'react';

/** Only list routes can be return targets. A detail link never accepts an arbitrary URL. */
export function safePersonReturn(value: string | null): string | null {
  if (!value || value.includes('\\') || value.includes('#') || /[\u0000-\u001f]/.test(value)) return null;
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    if (url.pathname !== '/' && url.pathname !== '/people' && url.pathname !== '/leaderboards' && url.pathname !== '/teams' && url.pathname !== '/clubs' && !/^\/clubs\/[A-Za-z0-9-]+$/.test(url.pathname)) return null;
    return `${url.pathname}${url.search}`;
  } catch { return null; }
}

export function personDetailUrl(id: string, source: string): string {
  const returnTo = safePersonReturn(source);
  const params = new URLSearchParams();
  if (returnTo) params.set('return_to', returnTo);
  return `/people/${encodeURIComponent(id)}${params.size ? `?${params}` : ''}`;
}

export function rememberListScroll(source: string): void {
  const path = safePersonReturn(source);
  if (!path) return;
  try { sessionStorage.setItem(`admin-list-scroll:${path}`, String(window.scrollY)); } catch { /* Navigation still works. */ }
}

/** Restore only after the list has rendered. Cleanup cancels pending work on navigation. */
export function useRestoreListScroll(source: string, ready: boolean): void {
  useEffect(() => {
    if (!ready) return;
    const path = safePersonReturn(source);
    if (!path) return;
    let raw: string | null;
    try { raw = sessionStorage.getItem(`admin-list-scroll:${path}`); } catch { return; }
    const target = Number(raw);
    if (raw === null || !Number.isFinite(target) || target < 0) return;
    const frame = requestAnimationFrame(() => {
      const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      window.scrollTo(0, Math.min(target, maxScroll));
      try { sessionStorage.removeItem(`admin-list-scroll:${path}`); } catch { /* Scroll already restored. */ }
    });
    return () => cancelAnimationFrame(frame);
  }, [source, ready]);
}
